import api from "@/lib/api";

/**
 * Which columns a download carries, and remembering what each person chose.
 *
 * The columns themselves come from the server (GET /exports/columns), the same registry the
 * files are written from — a list kept here as well would be one more copy of a vocabulary,
 * and this project has watched three copies of one drift apart. What lives here is only the
 * person's choice, in this browser.
 *
 * A choice is remembered per document AND per format: a six-column PDF for the noticeboard
 * must not quietly cut next month's Excel, the file HR pivots, down to the same six.
 */

const STORAGE_KEY = "abhyas.export-columns.v1";

let pending = null;

const isColumn = (c) => !!c && typeof c === "object" && typeof c.key === "string" && typeof c.label === "string";
const isDocument = (d) => !!d && typeof d === "object" && !Array.isArray(d)
  && Array.isArray(d.columns) && d.columns.length > 0 && d.columns.every(isColumn);
const isColumnSet = (v) => !!v && typeof v === "object" && !Array.isArray(v)
  && Object.keys(v).length > 0 && Object.values(v).every(isDocument);

/** Every document's columns, asked for once per page load. */
export function fetchExportColumns() {
  if (!pending) {
    pending = api.get("/exports/columns").then((r) => {
      // Trusted by shape, not by status: an HTML error page answering 200 where JSON belongs
      // would otherwise open a dialog with nothing in it.
      if (!isColumnSet(r.data)) throw new Error("The list of columns did not arrive whole.");
      return r.data;
    });
    // Forgotten on failure, or one bad moment would answer every later click — Try again
    // included — with the same failure for as long as the page stays open.
    pending.catch(() => { pending = null; });
  }
  return pending;
}

const keysOf = (doc) => (doc?.columns || []).map((c) => c.key);
const requiredOf = (doc) => (doc?.columns || []).filter((c) => c.required).map((c) => c.key);

/** The keys asked for, in the DOCUMENT's order and with its required columns — whatever order they were ticked in. */
export function inDocumentOrder(doc, keys) {
  const keep = new Set([...keys, ...requiredOf(doc)]);
  return keysOf(doc).filter((k) => keep.has(k));
}

/** What a document starts with in a format nobody has chosen for yet. */
export function defaultSelection(doc, format) {
  const exists = new Set(keysOf(doc));
  const named = (Array.isArray(doc?.defaults?.[format]) ? doc.defaults[format] : []).filter((k) => exists.has(k));
  // A format with no default named starts with everything, rather than with nothing to download.
  return inDocumentOrder(doc, named.length ? named : keysOf(doc));
}

function readAll() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    // A private window can throw on the read itself; nothing remembered is the honest answer.
    return {};
  }
}

function writeAll(all) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Storage full or switched off: the download still happens, it just is not remembered.
  }
}

function entryOf(all, docKey) {
  const entry = all[docKey];
  return entry && typeof entry === "object" && !Array.isArray(entry) ? entry : {};
}

const strings = (v) => (Array.isArray(v) ? v.filter((k) => typeof k === "string") : null);

/**
 * The columns this person last chose for this document in this format, else the default.
 *
 * Stored beside the choice is every column that existed when it was made (`known`), because
 * "they unticked it" and "it did not exist yet" are otherwise the same absence. A column the
 * server added since arrives at its DEFAULT state — not silently unchecked, which is how a
 * new column would stay invisible to exactly the people who download most. A remembered
 * column that no longer exists is dropped.
 */
export function loadSelection(docKey, format, doc) {
  const stored = entryOf(readAll(), docKey).formats?.[format];
  const saved = strings(stored?.selected);
  if (!saved) return defaultSelection(doc, format);
  const exists = new Set(keysOf(doc));
  const kept = saved.filter((k) => exists.has(k));
  // Nothing of the old choice survives: the default, not a lone required column.
  if (!kept.length) return defaultSelection(doc, format);
  const known = new Set(strings(stored.known) || saved);
  const byDefault = new Set(defaultSelection(doc, format));
  const unseen = keysOf(doc).filter((k) => !known.has(k) && byDefault.has(k));
  return inDocumentOrder(doc, [...kept, ...unseen]);
}

/**
 * Remember a choice. A choice that IS the default is not stored at all, so a default the
 * server changes later still reaches this person instead of being frozen by "Reset".
 */
export function saveSelection(docKey, format, keys, doc) {
  const chosen = inDocumentOrder(doc, keys);
  const byDefault = defaultSelection(doc, format);
  const same = chosen.length === byDefault.length && chosen.every((k, i) => k === byDefault[i]);
  const all = readAll();
  const entry = entryOf(all, docKey);
  const formats = { ...(entry.formats && typeof entry.formats === "object" ? entry.formats : {}) };
  if (same) delete formats[format];
  else formats[format] = { selected: chosen, known: keysOf(doc) };
  all[docKey] = { ...entry, formats };
  writeAll(all);
}

export function clearSelection(docKey, format) {
  const all = readAll();
  const entry = entryOf(all, docKey);
  if (!entry.formats || !(format in entry.formats)) return;
  const formats = { ...entry.formats };
  delete formats[format];
  all[docKey] = { ...entry, formats };
  writeAll(all);
}

/** The format this person last downloaded this document in, if the document still offers it. */
export function lastFormat(docKey, formats) {
  const last = entryOf(readAll(), docKey).last;
  return typeof last === "string" && (formats || []).includes(last) ? last : null;
}

export function rememberFormat(docKey, format) {
  const all = readAll();
  all[docKey] = { ...entryOf(all, docKey), last: format };
  writeAll(all);
}

/**
 * The address of a server-rendered document: `base` without its extension, the page's filters
 * as they already are in the address bar, then the chosen columns as repeated `columns=` keys.
 * One builder, so no caller can send the filters to one format and not the other.
 */
export function documentHref(base, search = "") {
  return (format, keys) => {
    const query = [search, ...(keys || []).map((k) => `columns=${encodeURIComponent(k)}`)].filter(Boolean).join("&");
    return `${base}.${format}${query ? `?${query}` : ""}`;
  };
}
